import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import { ChevronDown, SlidersHorizontal } from 'lucide-react';
import CustomSelect from './ui/CustomSelect';
import { useMobileShellContext } from '../contexts/MobileShellContext';
import { PERIOD_OPTIONS } from '../hooks/useContextFilters';

interface MobileFilterSheetProps {
  selectedBrandLabel: string;
  selectedBranchLabel: string;
  selectedPeriodLabel: string;
  brandOptions: { value: string | number; label: string }[];
  branchOptions: { value: string | number; label: string }[];
  selectedBrandValue: { value: string | number; label: string } | null;
  selectedBranchValue: { value: string | number; label: string } | null;
  selectedPeriodValue: { value: string; label: string };
  onBrandChange: (option: { value: string | number; label: string } | null) => void;
  onBrandSearchChange: (query: string) => void;
  onBranchChange: (option: { value: string | number; label: string } | null) => void;
  onPeriodChange: (option: { value: string | number; label: string } | null) => void;
  loading: boolean;
  disablePeriodSelector?: boolean;
  disableBranchSelector?: boolean;
  hasBrand: boolean;
}

const MobileFilterSheet: React.FC<MobileFilterSheetProps> = ({
  selectedBrandLabel,
  selectedBranchLabel,
  selectedPeriodLabel,
  brandOptions,
  branchOptions,
  selectedBrandValue,
  selectedBranchValue,
  selectedPeriodValue,
  onBrandChange,
  onBrandSearchChange,
  onBranchChange,
  onPeriodChange,
  loading,
  disablePeriodSelector = false,
  disableBranchSelector = false,
  hasBrand,
}) => {
  const { filterSheetOpen, openFilterSheet, closeFilterSheet } = useMobileShellContext();

  useEffect(() => {
    if (!filterSheetOpen) return;
    document.body.classList.add('overlay-scroll-lock');
    return () => document.body.classList.remove('overlay-scroll-lock');
  }, [filterSheetOpen]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && filterSheetOpen) {
        closeFilterSheet();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [filterSheetOpen, closeFilterSheet]);

  const summaryParts = [selectedBrandLabel, selectedBranchLabel, selectedPeriodLabel].filter(Boolean);
  const summaryText = summaryParts.length > 0
    ? summaryParts.join(' • ')
    : 'انتخاب برند، شعبه و دوره';

  const sheetOverlay = filterSheetOpen
    ? ReactDOM.createPortal(
        <>
          <button
            type="button"
            className="mobile-filter-sheet-backdrop overlay-drawer fixed inset-0 bg-black/50 mobile-only-shell"
            aria-label="بستن فیلترها"
            onClick={closeFilterSheet}
          />
          <div
            id="mobile-filter-sheet-panel"
            role="dialog"
            aria-modal="true"
            aria-label="تغییر برند، شعبه و دوره"
            className="mobile-filter-sheet-panel overlay-filter-sheet mobile-only-shell fixed inset-x-0 bottom-0 rounded-t-[var(--radius-2xl,1rem)]"
          >
            <div className="mobile-filter-sheet-handle" aria-hidden="true" />

            <div className="mobile-filter-sheet-header flex items-center justify-between px-4 py-3">
              <h2 className="text-base font-semibold text-content-primary">فیلترها</h2>
              <button
                type="button"
                className="rounded-lg px-3 py-2 text-sm font-medium text-content-secondary transition-colors hover:bg-surface-secondary"
                onClick={closeFilterSheet}
                style={{ minHeight: 'var(--touch-target-min, 44px)' }}
              >
                بستن
              </button>
            </div>

            <div className="mobile-filter-sheet-body" dir="rtl">
              <div className="mobile-filter-field mobile-filter-select w-full">
                <span className="mobile-filter-field__label">برند</span>
                <CustomSelect
                  options={brandOptions}
                  value={selectedBrandValue}
                  onChange={onBrandChange}
                  placeholder="انتخاب برند"
                  isSearchable
                  isLoading={loading}
                  onSearchChange={onBrandSearchChange}
                  searchPlaceholder="جستجو برند یا کد شعبه"
                  fullWidth
                  inMobileSheet
                />
              </div>
              <div className="mobile-filter-field mobile-filter-select w-full">
                <span className="mobile-filter-field__label">شعبه</span>
                <CustomSelect
                  options={branchOptions}
                  value={selectedBranchValue}
                  onChange={onBranchChange}
                  placeholder="انتخاب شعبه"
                  isSearchable
                  isLoading={loading}
                  isDisabled={disableBranchSelector || !hasBrand || branchOptions.length === 0}
                  fullWidth
                  inMobileSheet
                />
              </div>
              {!disablePeriodSelector && (
                <div className="mobile-filter-field mobile-filter-select w-full">
                  <span className="mobile-filter-field__label">دوره</span>
                  <CustomSelect
                    options={PERIOD_OPTIONS}
                    value={selectedPeriodValue}
                    onChange={onPeriodChange}
                    placeholder="انتخاب دوره"
                    fullWidth
                    inMobileSheet
                  />
                </div>
              )}
            </div>

            <div className="mobile-filter-sheet-footer">
              <button
                type="button"
                className="mobile-filter-apply-btn"
                onClick={closeFilterSheet}
              >
                اعمال
              </button>
            </div>
          </div>
        </>,
        document.body
      )
    : null;

  return (
    <>
      <div className="mobile-filter-bar mobile-only-shell sticky z-30" style={{ top: 'var(--layout-header-height, 5rem)' }}>
        <button
          type="button"
          className="mobile-filter-bar__trigger"
          dir="rtl"
          onClick={openFilterSheet}
          aria-expanded={filterSheetOpen}
          aria-controls="mobile-filter-sheet-panel"
          aria-label={`تغییر فیلتر: ${summaryText}`}
        >
          <SlidersHorizontal size={17} strokeWidth={2} aria-hidden="true" className="mobile-filter-bar__trigger-icon" />
          <span className="mobile-filter-bar__trigger-summary" title={summaryText}>
            {summaryText}
          </span>
          <ChevronDown
            size={16}
            strokeWidth={2}
            aria-hidden="true"
            className={`mobile-filter-bar__trigger-chevron${filterSheetOpen ? ' mobile-filter-bar__trigger-chevron--open' : ''}`}
          />
        </button>
      </div>

      {sheetOverlay}
    </>
  );
};

export default MobileFilterSheet;
